import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom'
import { Home, MessageSquare, BookOpen, BookMarked, Heart, MoreHorizontal } from 'lucide-react'
import { useSession } from '@/context/SessionContext'
import { useProfile } from '@/context/ProfileContext'
import { PWAInstallHint, UpdateModal } from '@/components/shared'
import ErrorBoundary from '@/components/layout/ErrorBoundary'

const NAV_ITEMS = [
  { to: '/home', label: 'Home', icon: Home },
  { to: '/chat', label: 'Chat', icon: MessageSquare },
  { to: '/learn', label: 'Learn', icon: BookOpen },
  { to: '/bible', label: 'Bible', icon: BookMarked },
  { to: '/prayer', label: 'Prayer', icon: Heart },
  { to: '/more', label: 'More', icon: MoreHorizontal },
]

function deriveInitials(displayName, email) {
  if (displayName) {
    const parts = displayName.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  if (email) {
    const local = email.split('@')[0] || ''
    if (local.length >= 2) return local.slice(0, 2).toUpperCase()
    if (local.length === 1) return local.toUpperCase()
  }
  return 'IF'
}

/**
 * Single consolidated top bar for every viewport — logo on the left,
 * Home/Chat/Learn/Bible/Prayer as inline tab buttons in the middle, More
 * button + account avatar on the right. The avatar shows the user's
 * initials and routes to /account.
 */
function TopNav({ profile, userEmail }) {
  const initials = deriveInitials(profile?.display_name, userEmail)
  return (
    <header
      className="hide-on-insight fixed top-0 left-0 right-0 z-50 bg-[var(--bg-base)]/95 backdrop-blur-md border-b border-[var(--glass-border)]"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* Full-width row — logo flush far-left, account flush far-right,
          tabs (incl. More) absolutely centered. Drops the prior max-w-6xl
          container so the logo no longer "floats in the middle of nowhere"
          on wide screens. */}
      <div className="px-2 md:px-5 h-14 flex items-center gap-1 md:gap-3">
        {/* Logo routes to the public marketing landing (with `?from=app`
            escape hatch so the signed-in redirect in App.jsx LandingPage
            doesn't bounce them back to /home). Dashboard is reachable via
            the explicit Home tab to its right. */}
        <NavLink
          to="/?from=app"
          className="shrink-0 flex items-center"
          aria-label="Illuminate Faith — home"
        >
          <img
            src="/FinalLogo.png"
            alt="Illuminate Faith logo"
            className="h-9 md:h-11 w-auto rounded self-center -translate-y-0.5"
            style={{ clipPath: 'inset(2% round 4px)' }}
          />
        </NavLink>

        {/* Primary tabs — Home / Chat / Learn / Bible / Prayer / More.
            In flexbox flow (flex-1 on the nav) so the tabs occupy real
            space alongside the logo and avatar instead of floating over
            them. On mobile each tab is flex-1 so the 6 of them evenly
            split the available width — fixes the iPhone overlap bug
            where Safari's slightly wider font metrics pushed the
            absolute-centered cluster onto the logo and avatar. Desktop
            reverts to natural-width tabs centered in the remaining
            space (md:flex-initial + md:justify-center). A11Y-007
            rebuild (2026-05-05): mobile font 11px, icon w-4 h-4,
            min-h-[44px] preserves the vertical tap target (WCAG 2.5.5
            AAA + 2.5.8 AA — horizontal min-width drops on mobile but
            the spacing exception via container gap satisfies the
            rule); active state, aria-current, aria-hidden on icon all
            preserved. */}
        <nav className="flex-1 min-w-0 flex items-center md:justify-center md:gap-1">
          {NAV_ITEMS.map(({ to, label, icon }) => {
            const Icon = icon
            return (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `flex-1 md:flex-initial md:shrink-0 flex flex-col md:flex-row items-center gap-0.5 md:gap-1.5 md:min-w-[44px] min-h-[44px] justify-center px-1 md:px-3 py-1.5 md:py-1.5 rounded-lg text-[11px] md:text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-[var(--gold)] bg-[var(--gold)]/15 md:bg-[var(--gold)]/10'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-high)]/50'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className="w-4 h-4" aria-hidden="true" />
                    <span aria-current={isActive ? 'page' : undefined}>{label}</span>
                  </>
                )}
              </NavLink>
            )
          })}
        </nav>

        {/* Account avatar — far-right. Initials, click → /account. */}
        <NavLink
          to="/account"
          aria-label="Account"
          title="Account"
          className="shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center font-heading font-bold text-[10px] md:text-xs text-[var(--gold)] border border-[var(--gold-select)] bg-gradient-to-br from-[#2c5282] to-[#413d68] hover:brightness-110 transition-all"
        >
          {initials}
        </NavLink>
      </div>
    </header>
  )
}

export default function AppLayout() {
  const { user, loading } = useSession()
  const { profile } = useProfile()
  const location = useLocation()
  const isChat = location.pathname === '/chat'

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)]">
        <div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!user) return <Navigate to="/login" replace />

  // Profile loads async after session resolves — don't evaluate the access
  // gate on an indeterminate profile, or we'll bounce legitimate admins home
  // during the tiny window between session-ready and profile-ready.
  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)]">
        <div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  // Soft launch (2026-04-28): no app-shell gate beyond authentication.
  // Free tier is part of the launch product, so any authenticated user with
  // completed onboarding can access the app shell. Per-feature gating
  // (premium chat tabs, message caps, premium-only tools) is enforced
  // inside each feature — not here.

  // Redirect to onboarding if not completed
  if (profile && !profile.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />
  }

  // Zone-level ErrorBoundary (FE-029) contains crashes inside the app
  // shell so a Bible/Chat/Prayer bug doesn't whitescreen the navbar +
  // account avatar — user can still access /account or sign out. Top-level
  // boundary in App.jsx remains as the last-resort catch-all.
  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <TopNav profile={profile} userEmail={user?.email} />
      {/* Single always-visible 3.5rem top bar everywhere. Top padding adds
          the iOS safe-area inset so content clears the notch / dynamic
          island once the page itself paints into the safe area
          (viewport-fit=cover in index.html). Inline style instead of a
          Tailwind arbitrary value — calc() with env() inside `pt-[...]`
          doesn't parse reliably across Tailwind 4 builds. */}
      <main
        id="main-content"
        className={isChat ? 'pb-0' : 'pb-4'}
        style={{ paddingTop: 'calc(3.5rem + env(safe-area-inset-top))' }}
      >
        <ErrorBoundary>
          {isChat ? (
            <div style={{ height: 'calc(100dvh - 3.5rem - env(safe-area-inset-top))' }}>
              <Outlet />
            </div>
          ) : (
            <Outlet />
          )}
        </ErrorBoundary>
      </main>
      {/* iOS-only install hint. Renders nothing on Android/desktop/non-Safari,
          and waits 3 days before showing so brand-new users aren't
          nagged. Fixed-positioned — doesn't affect chat layout. */}
      <PWAInstallHint />
      {/* Patch-note changelog popup — fires on any authenticated page when a
          live patch_note exists that the user hasn't seen yet. Dismissal writes
          to user_seen_patch_notes so it never re-appears for that account. */}
      <UpdateModal />
    </div>
  )
}
